import { type HermesProfileCatalog, type SessionSummary } from "@herskin/contracts";
import { IconButton } from "@herskin/ui";
import {
  IconBellRinging as BellRinging,
  IconPlus as Plus,
  IconSearch as Search,
} from "@tabler/icons-react";
import type { ComponentProps } from "react";
import { ProfileSwitcher, type ProfileScope } from "./ProfileSwitcher";
import { SessionGroups } from "./SessionGroups";

type SessionGroupProps = ComponentProps<typeof SessionGroups>;

interface SessionRailProps {
  feedback: { tone: "success" | "warning" | "danger"; message: string } | null;
  hermesProfiles: HermesProfileCatalog | null;
  profileScope: ProfileScope;
  profilePickerRequest: number;
  sessions: SessionSummary[];
  scopedSessions: SessionSummary[];
  defaultAgentName: string;
  searchOpen: boolean;
  taskPushOpen: boolean;
  taskPushUnread: number;
  selectedId: string | null;
  busySessionId: string | null;
  onToggleSearch: () => void;
  onCloseSearch: () => void;
  onCreateSession: () => void;
  onOpenTaskPush: () => void;
  onManageProfiles: () => void;
  onSelectProfileScope: NonNullable<ComponentProps<typeof ProfileSwitcher>["onSelect"]>;
  onSelectSession: NonNullable<SessionGroupProps["onSelect"]>;
  onRenameSession: NonNullable<SessionGroupProps["onRename"]>;
  onArchiveSession: NonNullable<SessionGroupProps["onArchive"]>;
  onRestoreSession: NonNullable<SessionGroupProps["onRestore"]>;
  onDeleteSession: NonNullable<SessionGroupProps["onDelete"]>;
}

/** 会话导航与 Profile 范围选择；聊天正文不需要知道列表的交互细节。 */
export function SessionRail({
  feedback,
  hermesProfiles,
  profileScope,
  profilePickerRequest,
  sessions,
  scopedSessions,
  defaultAgentName,
  searchOpen,
  taskPushOpen,
  taskPushUnread,
  selectedId,
  busySessionId,
  onToggleSearch,
  onCloseSearch,
  onCreateSession,
  onOpenTaskPush,
  onManageProfiles,
  onSelectProfileScope,
  onSelectSession,
  onRenameSession,
  onArchiveSession,
  onRestoreSession,
  onDeleteSession,
}: SessionRailProps) {
  return (
    <aside className="session-rail">
      <div className="rail-header">
        <div>
          <span className="section-kicker">工作空间</span>
          <h2>会话</h2>
        </div>
        <div className="rail-actions">
          <IconButton aria-label="搜索会话" aria-expanded={searchOpen} onClick={onToggleSearch}>
            <Search />
          </IconButton>
          <IconButton aria-label="新建会话" tone="accent" onClick={onCreateSession}>
            <Plus />
          </IconButton>
        </div>
      </div>
      {feedback ? (
        <div
          className={`session-feedback session-feedback--${feedback.tone}`}
          role={feedback.tone === "danger" ? "alert" : "status"}
        >
          {feedback.message}
        </div>
      ) : null}
      <ProfileSwitcher
        catalog={hermesProfiles}
        scope={profileScope}
        sessions={sessions}
        defaultDisplayName={defaultAgentName}
        openRequest={profilePickerRequest}
        onSelect={onSelectProfileScope}
        onManage={onManageProfiles}
      />
      <nav className="session-navigation" aria-label="Hermes 会话">
        {!searchOpen ? (
          <button
            type="button"
            className={`task-push-session ${taskPushOpen ? "task-push-session--active" : ""}`}
            aria-current={taskPushOpen ? "page" : undefined}
            onClick={onOpenTaskPush}
          >
            <span className="session-icon">
              <BellRinging size={16} />
            </span>
            <span className="session-copy">
              <strong>任务推送</strong>
              <small>查看马思思推送给老板的消息</small>
            </span>
            {taskPushUnread ? <span className="unread-count">{taskPushUnread}</span> : null}
          </button>
        ) : null}
        <div className="session-list">
          <SessionGroups
            sessions={scopedSessions}
            selectedId={taskPushOpen ? null : selectedId}
            busySessionId={busySessionId}
            onSelect={onSelectSession}
            onRename={onRenameSession}
            onArchive={onArchiveSession}
            onRestore={onRestoreSession}
            onDelete={onDeleteSession}
            searchOpen={searchOpen}
            onSearchClose={onCloseSearch}
          />
        </div>
      </nav>
    </aside>
  );
}
